import { useEffect, useMemo, useState } from 'react'
import {
    DropEvent,
    FileError,
    FileRejection,
    FileWithPath,
    useDropzone,
} from 'react-dropzone'
import { validate } from 'uuid'
import { useIntl } from 'react-intl'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Button from 'components/shared/Button'
import UploadedPhoto from 'routes/unlimited-card/CardConfiguration/UnlimitedCardConfigurator/UploadedPhoto'
import UploadedPhotoErrorMessage from 'routes/unlimited-card/CardConfiguration/UnlimitedCardConfigurator/UploadedPhotoErrorMessage'
import Instructions from './InstructionsAndExamples/Instructions'
import Examples from './InstructionsAndExamples/Examples'
import RemovePhotoModal from './RemovePhotoModal'
import { FileWithProps, URLwithStore } from 'routes/unlimited-card/helpers'
import { useResolutions } from 'store/resolutions'
import { useApp } from 'store/steps'
import { UploaderWrapper } from './styles'
import { setCardDetails } from 'store/unlimited-slice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useUnlimited } from 'store/unlimited-slice/hooks'
import { useCart } from 'store/cart-slice/hooks'
import photoInputPl from 'assets/images/unlimitedCard/photoInput_bg-pl.svg'
import photoInputEn from 'assets/images/unlimitedCard/photoInput_bg-en.svg'
import photoInputUk from 'assets/images/unlimitedCard/photoInput_bg-uk.svg'

interface OwnerPhotoProps {
    index: number
}

const OwnerPhoto = ({ index }: OwnerPhotoProps) => {
    const dispatch = useAppDispatch()

    const { isTablet } = useResolutions()
    const { language } = useApp()
    const { isCartEmpty, isSummaryFetching } = useCart()
    const { formatMessage } = useIntl()
    const { clearItemsFromCart, isProcessing } = useUnlimited()

    const { cardBoxes } = useAppSelector((state) => state.unlimited)

    const [file, setFile] = useState<FileWithProps | null>(null)

    const [isModalOpened, setIsModalOpened] = useState(false)

    const [isFormatError, setIsFormatError] = useState(false)
    const [isResolutionError, setIsResolutionError] = useState(false)
    const [isWeightError, setIsWeightError] = useState(false)

    const { image, originalImage } = cardBoxes[index]

    const MIN_WIDTH = 492
    const MIN_HEIGHT = 633

    const uploaderBg = useMemo(() => {
        switch (language) {
            case 'en':
                return photoInputEn
            case 'uk':
                return photoInputUk
            case 'pl':
            default:
                return photoInputPl
        }
    }, [language])

    const cleanFileMemory = () => {
        // eslint-disable-next-line compat/compat
        file && file.preview && URL.revokeObjectURL(file.preview)
    }

    const handleSwitchModal = () => {
        setIsModalOpened(!isModalOpened)
    }

    const handleRemovePhoto = () => {
        cleanFileMemory()
        setFile(null)
        dispatch(setCardDetails({ field: 'image', value: '', index }))
        handleSwitchModal()
    }

    const handleOnDrop = (acceptedFiles: FileWithPath[]) => {
        const acceptedFile = acceptedFiles[0]

        if (!acceptedFile) return

        setFile(
            Object.assign(acceptedFile, {
                // eslint-disable-next-line compat/compat
                preview: URLwithStore.createObjectURL(acceptedFile),
                width: 0,
                height: 0,
            })
        )
    }

    const handleOnDropRejected = (rejectedFiles: FileRejection[]) => {
        rejectedFiles.map((file: FileRejection) =>
            file.errors.map((error: FileError) => {
                if (error.code === 'file-invalid-type') setIsFormatError(true)
                else if (error.code === 'file-too-large') setIsWeightError(true)
                else if (error.code === 'small-width-height')
                    setIsResolutionError(true)
                return error
            })
        )
    }

    const handleGetFilesFromEvent: (
        event: DropEvent
    ) => Promise<(File | DataTransferItem)[]> = async (event: DropEvent) => {
        const files =
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            event.target.files || event.dataTransfer.files
        const promises = []

        setIsFormatError(false)
        setIsResolutionError(false)
        setIsWeightError(false)

        for (let index = 0; index < files.length; index++) {
            const file = files[index]
            // eslint-disable-next-line compat/compat
            const promise: Promise<File | DataTransferItem> = new Promise(
                (resolve, reject) => {
                    let fileType = ''
                    const fileReader = new FileReader()

                    fileReader.onloadend = (event: ProgressEvent) => {
                        const arr = new Uint8Array(
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            event?.target?.result
                        ).subarray(0, 4)
                        let header = ''
                        for (let ind = 0; ind < arr.length; ind++) {
                            header += arr[ind].toString(16)
                        }

                        switch (header) {
                            case '89504e47':
                                fileType = 'image/png'
                                break
                            case 'ffd8ffe0':
                            case 'ffd8ffe1':
                            case 'ffd8ffe2':
                            case 'ffd8ffe3':
                            case 'ffd8ffe8':
                                fileType = 'image/jpeg'
                                break
                            default:
                                fileType = 'unknown'
                                break
                        }

                        if (fileType === 'unknown') {
                            setIsFormatError(true)
                            reject({
                                code: 'file-invalid-type',
                                message:
                                    'You uploaded a photo in an unsupported format. Upload a photo in PNG, JPG or JPEG format.',
                            })
                            return
                        }

                        const image = new Image()
                        let url: string

                        image.onload = function () {
                            if (
                                image.width < MIN_WIDTH ||
                                image.height < MIN_HEIGHT
                            ) {
                                setIsResolutionError(true)
                                reject({
                                    code: 'small-width-height',
                                    message: `Image dimensions must be greater than ${MIN_WIDTH}x${MIN_HEIGHT}`,
                                })
                                return
                            }
                            resolve(file)
                        }

                        // eslint-disable-next-line compat/compat, prefer-const
                        url = URL.createObjectURL(file)
                        image.src = url
                    }
                    fileReader.readAsArrayBuffer(file)
                }
            )
            promises.push(promise)
        }
        // eslint-disable-next-line compat/compat
        return await Promise.all(promises)
    }

    const { getRootProps, getInputProps, open } = useDropzone({
        accept: {
            'image/png': ['.png'],
            'image/jpg': ['.jpg'],
            'image/jpeg': ['.jpeg'],
        },
        maxFiles: 1,
        maxSize: 5242884,
        multiple: false,
        useFsAccessApi: false,
        onDrop: handleOnDrop,
        onDropRejected: handleOnDropRejected,
        getFilesFromEvent: handleGetFilesFromEvent,
    })

    const uploadCta = isTablet ? (
        <Button
            variant="secondary"
            width="100%"
            hasArrow={false}
            as="label"
            htmlFor="pictureUploader"
        >
            {formatMessage({
                id: 'annual.btn.upload',
                defaultMessage: 'Wgraj zdjęcie',
            })}
        </Button>
    ) : (
        <label htmlFor="pictureUploader">
            <img
                src={uploaderBg}
                alt=""
                data-testid={`unlimited-${index}-trigger-photo-choose`}
            />
        </label>
    )

    useEffect(() => {
        if (!isCartEmpty && !isProcessing && !isSummaryFetching) {
            // check if image on every card is set and has full url
            // - not only uuid for restoring
            const areAllImagesReadyToEdit = cardBoxes.every(
                ({ image }) => image && !validate(image)
            )

            if (areAllImagesReadyToEdit) clearItemsFromCart()
        }
    }, [image])

    useEffect(() => {
        if (file) {
            setIsFormatError(false)
            setIsResolutionError(false)
            setIsWeightError(false)
        }
    }, [file])

    useEffect(() => {
        async function uploadCartImgToEditor(
            url: string,
            type: 'remote' | 'local'
        ) {
            let data: Blob

            if (type === 'remote') {
                // eslint-disable-next-line compat/compat
                const response = await fetch(url)
                data = await response.blob()
            } else {
                data = URLwithStore.getFromObjectURL(url)
            }
            const metadata = {
                type: 'image/jpeg',
            }
            const imgFile = new File([data], url, metadata)

            setFile(
                Object.assign(imgFile as FileWithProps, {
                    // eslint-disable-next-line compat/compat
                    preview: URLwithStore.createObjectURL(imgFile),
                    width: 0,
                    height: 0,
                })
            )
        }

        // on start, with data preloaded to editing...
        // ...try to use original image
        if (originalImage && originalImage.image)
            uploadCartImgToEditor(originalImage.image, 'local')
        else if (image) {
            // ...or to download already edited image from the server
            const cartImgUrl = `${process.env.REACT_APP_API_URL}api/${language}/cart/item/card/image/${image}`
            uploadCartImgToEditor(cartImgUrl, 'remote')
        }

        dispatch(
            setCardDetails({
                field: 'image',
                value: '',
                index,
            })
        )
    }, [])

    useEffect(() => () => cleanFileMemory(), [])

    return (
        <Box align="stretch">
            <Instructions />
            <Box isFullWidth direction={['column', 'row']} align="center">
                <UploaderWrapper
                    direction="column"
                    isFullWidth
                    mr={[0, 48]}
                    isVisible={!file}
                >
                    <Box isFullWidth mb={[8, 16]} align="center">
                        <Box {...getRootProps()} isFullWidth={isTablet}>
                            <input {...getInputProps()} />
                            {uploadCta}
                        </Box>
                    </Box>

                    <Box isFullWidth align="center">
                        <Text variant="bodyExtraSmall" color="grayscale_700">
                            {formatMessage({
                                id: 'annual.uploader.formats_info',
                                defaultMessage:
                                    'Wspierane formaty: PNG, JPG, JPEG',
                            })}
                        </Text>
                        <Text variant="bodyExtraSmall" color="grayscale_700">
                            {formatMessage({
                                id: 'annual.uploader.max_size_info',
                                defaultMessage: 'Maksymalny rozmiar: 5 MB',
                            })}
                        </Text>
                    </Box>
                </UploaderWrapper>
                {file && (
                    <UploadedPhoto
                        index={index}
                        file={file}
                        deletePhoto={handleSwitchModal}
                        changePhoto={open}
                    />
                )}
                <Examples />
            </Box>

            {/* TODO: get error/info messages also from API */}
            <UploadedPhotoErrorMessage
                isPhotoFormatError={isFormatError}
                isPhotoResolutionError={isResolutionError}
                isPhotoWeightError={isWeightError}
            />

            <RemovePhotoModal
                isOpened={isModalOpened}
                onLinkClick={() => handleSwitchModal()}
                onButtonClick={() => handleRemovePhoto()}
            />
        </Box>
    )
}

export default OwnerPhoto
